<!-- Fullscreen Header -->
<div class="fullscreen-header bg-primary text-white d-flex align-items-center justify-content-between">
  <div>
    <span class="session-header-title">Full Screen Floor View - {{ floorMapEntity?.name }}</span>
  </div>
  <button type="button" class="close-button" aria-label="Close" (click)="close.emit()">×</button>
</div>

<!-- Loading overlay -->
@if (blenderViewLoading) {
  <div class="blender-loading-overlay">
    <i class="fa-solid fa-spinner fa-spin"></i>
    <span>Loading 3D Floor...</span>
  </div>
}

<!-- Donut Chart Overlay (Top-Left of fullscreen mode) -->
<div class="donut-overlay-container" (click)="toggleRoomsList()">
  <div class="donut-container">
    <span class="chart-label">Floor Overall</span>

    <svg width="70" height="70" viewBox="0 0 42 42" class="donut-chart">
      <!-- Base Circle -->
      <circle cx="21" cy="21" r="15.915" fill="transparent" stroke="rgba(255,255,255,0.1)" stroke-width="3.5"></circle>

      <!-- Segments -->
      @if (currentFloorOrRoomStats.danger > 0) {
        <circle cx="21" cy="21" r="15.915" fill="transparent"
          stroke="#e74c3c" stroke-width="3.5"
          [attr.stroke-dasharray]="getDashArray(currentFloorOrRoomStats.danger, currentFloorOrRoomStats.total)"
        [attr.stroke-dashoffset]="getDashOffset(0, currentFloorOrRoomStats.total)"></circle>
      }

      @if (currentFloorOrRoomStats.safe > 0) {
        <circle cx="21" cy="21" r="15.915" fill="transparent"
          stroke="#2ecc71" stroke-width="3.5"
          [attr.stroke-dasharray]="getDashArray(currentFloorOrRoomStats.safe, currentFloorOrRoomStats.total)"
        [attr.stroke-dashoffset]="getDashOffset(currentFloorOrRoomStats.danger, currentFloorOrRoomStats.total)"></circle>
      }

      @if (currentFloorOrRoomStats.unknown > 0) {
        <circle cx="21" cy="21" r="15.915" fill="transparent"
          stroke="#d4a017" stroke-width="3.5"
          [attr.stroke-dasharray]="getDashArray(currentFloorOrRoomStats.unknown, currentFloorOrRoomStats.total)"
        [attr.stroke-dashoffset]="getDashOffset(currentFloorOrRoomStats.danger + currentFloorOrRoomStats.safe, currentFloorOrRoomStats.total)"></circle>
      }

      @if (currentFloorOrRoomStats.suspect > 0) {
        <circle cx="21" cy="21" r="15.915" fill="transparent"
          stroke="#f1c40f" stroke-width="3.5"
          [attr.stroke-dasharray]="getDashArray(currentFloorOrRoomStats.suspect, currentFloorOrRoomStats.total)"
        [attr.stroke-dashoffset]="getDashOffset(currentFloorOrRoomStats.danger + currentFloorOrRoomStats.safe + currentFloorOrRoomStats.unknown, currentFloorOrRoomStats.total)"></circle>
      }

      <!-- Text in Center -->
      <g class="chart-text">
        <text x="50%" y="50%" class="chart-number" text-anchor="middle" dominant-baseline="central">
          {{ currentFloorOrRoomStats.total }}
        </text>
      </g>
    </svg>

    <!-- Legend with Counts -->
    <div class="donut-legend">
      @if (currentFloorOrRoomStats.danger > 0) {
        <span class="legend-item danger">
          <span class="legend-color"></span>{{ currentFloorOrRoomStats.danger }}
        </span>
      }
      @if (currentFloorOrRoomStats.safe > 0) {
        <span class="legend-item safe">
          <span class="legend-color"></span>{{ currentFloorOrRoomStats.safe }}
        </span>
      }
      @if (currentFloorOrRoomStats.unknown > 0) {
        <span class="legend-item unknown">
          <span class="legend-color"></span>{{ currentFloorOrRoomStats.unknown }}
        </span>
      }
      @if (currentFloorOrRoomStats.suspect > 0) {
        <span class="legend-item suspect">
          <span class="legend-color"></span>{{ currentFloorOrRoomStats.suspect }}
        </span>
      }
    </div>
  </div>
</div>

<!-- Sliding Side Panel (List of rooms and users) -->
@if (showRoomsList) {
  <div class="rooms-sidebar-panel">
    <div class="rooms-sidebar-header">
      <h5>Rooms & Users</h5>
      <button type="button" class="close-btn-sidebar" (click)="toggleRoomsList(); $event.stopPropagation()">×</button>
    </div>
    <div class="rooms-list">
      @for (room of getRoomsOnFloor(); track room) {
        <div class="room-item-wrapper">
          <div class="floor-row room-floor-row" [class.active]="selectedRoomId === room.id">
            <div
              class="floor-label room-label"
              (click)="selectRoomAll(room.id); $event.stopPropagation()"
              title="Show all users in this room">
              {{ room.name }}
            </div>
            <div class="color-boxes">
              <div
                class="color-box danger-incident"
                [class.active-filter]="selectedRoomId === room.id && selectedRoomStatusFilter === 'danger'"
                (click)="selectRoomStatus(room.id, 'danger'); $event.stopPropagation()"
                title="Show users in danger">
                {{ getRoomStatusCounts(room.id).danger }}
              </div>
              <div
                class="color-box safe-incident"
                [class.active-filter]="selectedRoomId === room.id && selectedRoomStatusFilter === 'safe'"
                (click)="selectRoomStatus(room.id, 'safe'); $event.stopPropagation()"
                title="Show safe users">
                {{ getRoomStatusCounts(room.id).safe }}
              </div>
              <div
                class="color-box unknown-incident-yellow"
                [class.active-filter]="selectedRoomId === room.id && selectedRoomStatusFilter === 'unknown'"
                (click)="selectRoomStatus(room.id, 'unknown'); $event.stopPropagation()"
                title="Show unknown users">
                {{ getRoomStatusCounts(room.id).unknown }}
              </div>
            </div>
          </div>
          <!-- Expanded User List for selected room -->
          @if (selectedRoomId === room.id) {
            <div class="room-users-panel">
              @if (!hasSelectedRoomUsers) {
                <div class="room-users-empty">
                  No users match this filter
                </div>
              }
              @if (hasSelectedRoomUsers) {
                <div class="room-users-table">
                  @for (col of selectedRoomUserCols; track col) {
                    <div class="room-user-col">
                      @for (u of col; track trackByUserId($index, u)) {
                        <div class="room-user-row" (click)="showUserInfo(u); $event.stopPropagation()">
                          <span class="room-user-name">{{ u.name }}</span>
                          <img [src]="u.src" class="room-user-status-icon" alt="" />
                          <img
                            [src]="u.visible ? 'assets/img/eyeVisible.svg' : 'assets/img/eyeHidden.svg'"
                            (mousedown)="toggleLocalUserVisibility(u); $event.stopPropagation()"
                            (click)="$event.stopPropagation()"
                            class="room-user-eye-icon"
                            [alt]="u.visible ? 'Hide user' : 'Show user'" />
                          </div>
                        }
                      </div>
                    }
                  </div>
                }
              </div>
            }
          </div>
        }
      </div>
    </div>
  }
